import { Component, inject, input, computed, signal, effect } from "@angular/core";
import { AuthService } from "../../../services/AuthService";
import { SubscriptionService } from "../../../services/subscriptionservice";
import { MatDialog } from "@angular/material/dialog";
import { DialogAuthComponent } from "../../dialogs/dialog-auth/dialog-auth";
import { LightPathSubscriptionDetails } from "../../../pages/subscription-detail/types";
import { HealthIndicator } from "../../models/HealthIndicator";
import { openSelfServiceDialog } from "../../dialogs/dialog-selfservice/dialog-selfservice.helpers";
import { TranslateModule } from "@ngx-translate/core";
import { AngularSvgIconModule } from "angular-svg-icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { ReadableSizePipe } from "../../../helpers/bitspipe/bitspipe";
import { SapTile } from "../sap-tile/sap-tile";
import { MessageService } from "../../../services/messageservice";

@Component({
  selector: "connection",
  templateUrl: "connection.html",
  imports: [TranslateModule, AngularSvgIconModule, MatTooltipModule, SapTile, ReadableSizePipe],
})
export class ConnectionComponent {
  private auth = inject(AuthService);
  private dialog = inject(MatDialog);
  private subscriptionService = inject(SubscriptionService);
  private msgsrv = inject(MessageService);

  protected circuit = input.required<LightPathSubscriptionDetails["circuits"][0]>();
  protected subscription = input.required<LightPathSubscriptionDetails>();
  protected connectionNumber = input(1);

  protected productType = computed(() => this.subscription().product.type);
  protected fromHealth = signal<HealthIndicator>(undefined);
  protected toHealth = signal<HealthIndicator>(undefined);

  protected fromHealthState = computed(() => this.fromHealth()?.status);
  protected toHealthState = computed(() => this.toHealth()?.status);

  private lastFromTraffic = computed(() => this.fromHealth()?.traffic);
  protected lastTrafficIn = computed(() => this.lastFromTraffic()?.in ?? 0);
  protected lastTrafficOut = computed(() => this.lastFromTraffic()?.out ?? 0);

  protected overflowMenuActive = false;

  protected endpoints = computed(() => this.circuit().endpoints);

  protected fromSap = computed(() => this.endpoints()[0]);
  protected toSap = computed(() => this.endpoints()[1]);

  protected fromCity = computed(() => this.fromSap().port.address.city);
  protected toCity = computed(() => this.toSap().port.address.city);

  private somePortsAreUntagged = computed(() =>
    this.endpoints().some((endpoint) => endpoint.port.portMode === "Untagged"),
  );
  protected isSelfServiceAvailable = computed(
    () =>
      this.auth.hasRole(["Infraverantwoordelijke"])
      && this.auth?.isCurrentOrganisation(this.subscription().customerId)
      && !this.somePortsAreUntagged(),
  );

  protected selfServiceEnabled = computed(() => !this.subscription().dirty);

  constructor() {
    effect(() => {
      const promises = this.endpoints().map((endpoint) =>
        this.subscriptionService.getHealthDataForSubscription(endpoint.port.subscriptionId),
      );
      Promise.all(promises).then(([fromHealth, toHealth]) => {
        this.fromHealth.set(fromHealth);
        this.toHealth.set(toHealth);
      });
    });
  }

  openSelfserviceDialog(type) {
    const roleCheckResult = this.auth.checkRoleAccess(this.productType(), "edit");
    if (!roleCheckResult.ok) {
      this.auth.roleEvent.emit(roleCheckResult);
      return;
    }

    if (!this.auth.selfServiceStarted()) {
      this.dialog.open(DialogAuthComponent, {
        data: {
          selfserviceState: { connectionNumber: this.connectionNumber },
          initialAction: type,
        },
      });
    } else {
      const data = {
        type,
        circuit: this.circuit(),
        subscription: this.subscription(),
        titleSuffix: this.connectionNumber(),
      };

      const dialogRef = openSelfServiceDialog(this.dialog, data);
      dialogRef.afterClosed().subscribe((event: string) => {
        this.msgsrv.setSelfServiceMessage(event);
      });
    }
  }
}
